import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, PlusCircle, Edit, Trash2, Search, User, Phone } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function MentorManagement({ user, onLogout }) {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMentor, setEditingMentor] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    description: '',
    phone_number: '',
    whatsapp_number: '',
    youtube_link: '',
    status: 'active',
    photo_url: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [helpPhoneNumber, setHelpPhoneNumber] = useState('');
  const [savingHelpNumber, setSavingHelpNumber] = useState(false);

  useEffect(() => {
    fetchMentors();
    fetchHelpPhoneNumber();
  }, []);

  const fetchHelpPhoneNumber = async () => {
    try {
      const response = await axios.get(`${API}/settings/help-phone`, { headers: getAuthHeaders() });
      setHelpPhoneNumber(response.data.phone_number || '');
    } catch (error) {
      console.error('Failed to fetch help phone number:', error);
    }
  };

  const saveHelpPhoneNumber = async () => {
    if (!helpPhoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    try {
      setSavingHelpNumber(true);
      await axios.post(`${API}/settings/help-phone`, 
        { phone_number: helpPhoneNumber },
        { headers: getAuthHeaders() }
      );
      toast.success('Help phone number saved successfully!');
    } catch (error) {
      console.error('Failed to save help phone number:', error);
      toast.error('Failed to save phone number');
    } finally {
      setSavingHelpNumber(false);
    }
  };

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${API}/mentors`, { headers: getAuthHeaders() });
      setMentors(response.data);
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
      toast.error('Failed to load mentors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMentor) {
        await axios.put(`${API}/mentors/${editingMentor.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Mentor updated successfully');
      } else {
        await axios.post(`${API}/mentors`, formData, { headers: getAuthHeaders() });
        toast.success('Mentor created successfully');
      }
      
      setShowAddDialog(false);
      setEditingMentor(null);
      setFormData({ full_name: '', description: '', phone_number: '', whatsapp_number: '', youtube_link: '', status: 'active', photo_url: '' });
      fetchMentors();
    } catch (error) {
      console.error('Error saving mentor:', error);
      toast.error('Failed to save mentor');
    }
  };

  const handleEdit = (mentor) => {
    setEditingMentor(mentor);
    setFormData({
      full_name: mentor.full_name,
      description: mentor.description || '',
      phone_number: mentor.phone_number,
      whatsapp_number: mentor.whatsapp_number || '',
      youtube_link: mentor.youtube_link || '',
      status: mentor.status,
      photo_url: mentor.photo_url || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (mentorId, mentorName) => {
    if (!window.confirm(`Are you sure you want to delete "${mentorName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/mentors/${mentorId}`, { headers: getAuthHeaders() });
      toast.success('Mentor deleted successfully');
      fetchMentors();
    } catch (error) {
      console.error('Error deleting mentor:', error);
      toast.error('Failed to delete mentor');
    }
  };

  const filteredMentors = mentors
    .filter(mentor => 
      mentor.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (mentor.description && mentor.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'full_name') {
        return a.full_name.localeCompare(b.full_name);
      } else if (sortBy === 'created_at') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="mentors"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="mentors">
      <div className="p-6 lg:p-8 space-y-6" data-testid="mentor-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-navy">Mentors Management</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Add and manage mentors for student support</p>
          </div>
          <Button 
            onClick={() => {
              setEditingMentor(null);
              setFormData({ full_name: '', description: '', phone_number: '', whatsapp_number: '', youtube_link: '', status: 'active', photo_url: '' });
              setShowAddDialog(true);
            }}
            className="bg-primary-blue hover:bg-[#1874CD] text-white flex items-center space-x-2 rounded-xl transition-smooth btn-press"
          >
            <PlusCircle className="w-4 h-4" strokeWidth={2} />
            <span>Add Mentor</span>
          </Button>
        </div>

        {/* Need Help Settings */}
        <Card className="border-0 card-shadow bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-navy">Need Help Settings</CardTitle>
                <p className="text-sm text-[#4D4D4D] font-light mt-1">Configure phone number for login page help button</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="help-phone" className="text-sm font-semibold text-navy mb-2 block">
                  Help Phone Number
                </Label>
                <Input
                  id="help-phone"
                  type="tel"
                  value={helpPhoneNumber}
                  onChange={(e) => setHelpPhoneNumber(e.target.value)}
                  placeholder="+91 9876543210"
                  className="h-12 border-2 border-green-200 rounded-xl focus:border-green-500 transition-smooth bg-white"
                />
                <p className="text-xs text-[#4D4D4D] mt-1 ml-1">
                  Students can call this number from the "Need Help?" button on login page
                </p>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={saveHelpPhoneNumber}
                  disabled={savingHelpNumber || !helpPhoneNumber.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold h-12 px-6 rounded-xl transition-smooth btn-press"
                >
                  {savingHelpNumber ? 'Saving...' : 'Save Number'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search and Sort */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A5A5A5]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search mentors..."
                  className="pl-10 h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-48 h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Recently Added</SelectItem>
                  <SelectItem value="full_name">Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Mentors List */}
        {filteredMentors.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-16 text-center">
              <Users className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Mentors Yet</h3>
              <p className="text-[#4D4D4D] mb-6 font-light">
                {searchTerm ? 'No mentors match your search criteria' : 'Create your first mentor to get started'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
                >
                  Add First Mentor
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMentors.map((mentor) => (
              <Card key={mentor.id} className="border-0 card-shadow bg-white rounded-2xl overflow-hidden card-hover transition-smooth">
                <CardContent className="p-0">
                  {/* Rectangular Photo */}
                  <div className="relative h-48 bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden">
                    {mentor.photo_url ? (
                      <img src={mentor.photo_url} alt={mentor.full_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-blue to-[#1874CD] flex items-center justify-center">
                        <User className="w-20 h-20 text-white opacity-50" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    {/* Mentor Name */}
                    <h3 className="font-bold text-navy text-lg mb-3 text-center">{mentor.full_name}</h3>
                    
                    {/* Contact Info */}
                    <div className="space-y-1 mb-4 text-sm text-[#4D4D4D]">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-3 h-3 text-primary-blue" />
                        <span className="truncate">{mentor.phone_number}</span>
                      </div>
                      {mentor.whatsapp_number && (
                        <div className="flex items-center space-x-2">
                          <span className="text-[#25D366] font-semibold">WA:</span>
                          <span className="truncate">{mentor.whatsapp_number}</span>
                        </div>
                      )}
                      {mentor.youtube_link && (
                        <div className="flex items-center space-x-2">
                          <span className="text-[#FF0000] font-semibold">YT:</span>
                          <span className="truncate text-xs">{mentor.youtube_link}</span>
                        </div>
                      )}
                    </div>

                  {/* Status */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      mentor.status === 'active' 
                        ? 'bg-[#E8F5E9] text-[#4CAF50]' 
                        : 'bg-[#FFEBEE] text-red-700'
                    }`}>
                      {mentor.status.charAt(0).toUpperCase() + mentor.status.slice(1)}
                    </span>
                    <span className="text-xs text-[#A5A5A5]">
                      {new Date(mentor.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleEdit(mentor)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-2 border-[#E7E7E7] text-primary-blue hover:border-primary-blue rounded-xl"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(mentor.id, mentor.full_name)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-2 border-red-200 text-red-600 hover:border-red-600 rounded-xl"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Mentor Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-navy">
                {editingMentor ? 'Edit Mentor' : 'Add New Mentor'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-[15px] font-semibold text-navy">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  placeholder="Enter mentor's full name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="photo_url" className="text-[15px] font-semibold text-navy">
                  Photo URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="photo_url"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  type="url"
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  placeholder="https://example.com/mentor-photo.jpg"
                />
                <p className="text-xs text-[#A5A5A5]">Upload photo and paste URL here (JPG/PNG)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number" className="text-[15px] font-semibold text-navy">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  required
                  type="tel"
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  placeholder="+91 98765 43210"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="whatsapp_number" className="text-[15px] font-semibold text-navy">
                  WhatsApp Number
                </Label>
                <Input
                  id="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  type="tel"
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  placeholder="+91 98765 43210"
                />
                <p className="text-xs text-[#A5A5A5]">Optional - Will show WhatsApp button to students</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="youtube_link" className="text-[15px] font-semibold text-navy">
                  YouTube Channel Link
                </Label>
                <Input
                  id="youtube_link"
                  value={formData.youtube_link}
                  onChange={(e) => setFormData({ ...formData, youtube_link: e.target.value })}
                  type="url"
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  placeholder="https://youtube.com/@channel"
                />
                <p className="text-xs text-[#A5A5A5]">Optional - Will show YouTube button to students</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className="text-[15px] font-semibold text-navy">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue bg-white">
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button 
                  type="button" 
                  onClick={() => setShowAddDialog(false)}
                  variant="outline" 
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
                >
                  {editingMentor ? 'Update Mentor' : 'Save Mentor'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}